import {PackagingSection} from "@/components/Layout"

// The "getting started" steps game (dashboard tracker). Each step deep-links
// to a section and spotlights the concrete action to perform there via a
// [data-step-target] attribute. `id` is the key persisted in the
// /user/setting `steps_tracker` payload (service_name "packaging").
export type PackagingStep = {
    id: string
    section: PackagingSection
    target: string // [data-step-target] value to spotlight
}

// Role → required steps. `isAccountRole` comes from usePermissions (lowercase
// role names); `modules` decides which product-creation button credits allow.
export const stepsForRole = (
    isAccountRole: (roles: string[]) => boolean,
    modules: any,
    // Healthchecks are OWNER/ADMIN-of-the-main-org only (see isMainOrgManager);
    // the step is dropped for everyone else so it never points at a tab they
    // cannot open. Defaults to true for callers that predate the gate.
    canUseHealthchecks: boolean = true,
): PackagingStep[] => {
    const createProduct: PackagingStep = {
        id: "create_product",
        section: "products",
        // Follow credits: GS1-only accounts create GS1 products.
        target: modules?.gs1 && !modules?.geoloc ? "create-product-gs1" : "create-product",
    }
    const downloadProduct: PackagingStep = {id: "download_product", section: "products", target: "download-product"}
    // Batch management steps (everyone but analysts): export a spreadsheet to
    // update products in bulk / import one to create products in bulk.
    const updateProductsBatch: PackagingStep = {id: "update_products_batch", section: "batches", target: "batch-export"}
    const createProductsBatch: PackagingStep = {id: "create_products_batch", section: "batches", target: "batch-import"}

    if (isAccountRole(["owner", "admin", "supervisor"])) return [
        {id: "create_brand", section: "brands", target: "create-brand"},
        {id: "create_range", section: "brands", target: "create-range"},
        createProduct,
        downloadProduct,
        ...(canUseHealthchecks
            ? [{id: "launch_healthchecks", section: "health", target: "launch-healthchecks"} as PackagingStep]
            : []),
        updateProductsBatch,
        createProductsBatch,
    ]
    if (isAccountRole(["member"])) return [createProduct, downloadProduct, updateProductsBatch, createProductsBatch]
    if (isAccountRole(["analyst"])) return [
        {id: "see_analytics", section: "products", target: "see-analytics"},
    ]
    return []
}
